import { get, isEmpty } from "lodash";
import React from "react";
import PropTypes from "prop-types";
import Alert from "antd/lib/alert";
import Button from "antd/lib/button";
import Form from "antd/lib/form";
import Input from "antd/lib/input";
import Tabs from "antd/lib/tabs";

import routeWithApiKeySession from "@/components/ApplicationArea/routeWithApiKeySession";
import Link from "@/components/Link";
import BigMessage from "@/components/BigMessage";
import PageHeader from "@/components/PageHeader";
import DashboardGrid from "@/components/dashboards/DashboardGrid";
import Filters from "@/components/Filters";

import { Dashboard } from "@/services/dashboard";
import location from "@/services/location";
import routes from "@/services/routes";

import logoUrl from "@/assets/images/Audimatic_icon_small.png";

import useDashboard from "./hooks/useDashboard";

import "./PublicDashboardPage.less";

const { TabPane } = Tabs;

function PublicDashboard({ dashboard }) {
  const { globalParameters, filters, setFilters, refreshDashboard, loadWidget, refreshWidget } = useDashboard(
    dashboard
  );
  const tabs = dashboard.getTabsDefs();
  const hasTabbedLayout = dashboard.hasTabbedLayout();
  const [activeTabId, setActiveTabId] = React.useState(() => dashboard.getActiveTabId(location.search.tab));
  const activeWidgets = dashboard.getWidgetsForTab(activeTabId, dashboard.widgets);
  const activeParameters = dashboard.getGlobalParametersForTab(activeTabId, globalParameters);
  const activeTabLayout = dashboard.getLayoutForTab(activeTabId);
  const layoutOverrides = activeWidgets.reduce((acc, widget) => {
    const widgetId = String(widget.id);
    const saved = activeTabLayout[widgetId];
    if (saved) {
      acc[widgetId] = {
        ...widget.options.position,
        ...saved,
      };
    }
    return acc;
  }, {});
  const parameterBars = dashboard.getParameterBarsDefs(activeParameters);
  const parameterAssignments = dashboard.options?.parameterBarAssignments || {};
  const parameterOrders = dashboard.options?.parameterBarOrders || {};

  React.useEffect(() => {
    setActiveTabId(dashboard.getActiveTabId(location.search.tab));
  }, [dashboard]);

  const onTabChange = key => {
    const next = dashboard.getActiveTabId(key);
    setActiveTabId(next);
    location.setSearch({ tab: hasTabbedLayout ? next : null }, true);
  };

  return (
    <div className="container p-t-10 p-b-20">
      <PageHeader title={dashboard.name} />
      {!isEmpty(filters) && (
        <div className="m-b-10 p-15 bg-white tiled">
          <Filters filters={filters} onChange={setFilters} />
        </div>
      )}
      {hasTabbedLayout && (
        <div className="m-b-10 p-15 bg-white tiled" data-test="PublicDashboardTabs">
          <Tabs activeKey={activeTabId} onChange={onTabChange} animated={false}>
            {tabs.map(tab => (
              <TabPane tab={tab.name} key={tab.id} />
            ))}
          </Tabs>
        </div>
      )}
      <div id="dashboard-container">
        <DashboardGrid
          dashboard={dashboard}
          widgets={activeWidgets}
          globalParameters={activeParameters}
          parameterBars={parameterBars}
          parameterAssignments={parameterAssignments}
          parameterOrders={parameterOrders}
          layoutOverrides={layoutOverrides}
          filters={filters}
          isEditing={false}
          isPublic
          onGlobalParametersValuesChange={refreshDashboard}
          onLoadWidget={loadWidget}
          onRefreshWidget={refreshWidget}
        />
      </div>
    </div>
  );
}

PublicDashboard.propTypes = {
  dashboard: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
};

class PublicDashboardPage extends React.Component {
  static propTypes = {
    token: PropTypes.string.isRequired,
    onError: PropTypes.func,
  };

  static defaultProps = {
    onError: () => {},
  };

  state = {
    loading: true,
    dashboard: null,
    authRequired: false,
    authError: "",
    authUsername: "",
    authPassword: "",
  };

  componentDidMount() {
    this.loadDashboard();
  }

  loadDashboard = () => {
    const { token } = this.props;
    const { authUsername, authPassword } = this.state;
    const auth =
      authUsername && authPassword
        ? {
            username: authUsername,
            password: authPassword,
          }
        : null;

    this.setState({ loading: true, authError: "" });
    Dashboard.getByToken({ token, auth })
      .then(dashboard => this.setState({ dashboard, loading: false, authRequired: false }))
      .catch(error => {
        const status = parseInt(get(error, "response.status"), 10);
        const msg = get(error, "response.data.message") || get(error, "response.data") || "";
        const msgStr = typeof msg === "string" ? msg : typeof msg?.message === "string" ? msg.message : "";
        const looksLikePublicAuthChallenge =
          typeof msgStr === "string" &&
          msgStr.toLowerCase().includes("public dashboard authentication required");

        const needsPublicLogin =
          status === 401 ||
          status === 403 ||
          looksLikePublicAuthChallenge;

        if (needsPublicLogin) {
          this.setState({
            loading: false,
            authRequired: true,
            authError: auth ? "Invalid username or password." : "",
          });
        } else {
          this.props.onError(error);
        }
      });
  };

  onAuthSubmit = event => {
    event.preventDefault();
    this.loadDashboard();
  };

  render() {
    const { loading, dashboard, authRequired, authError, authUsername, authPassword } = this.state;
    return (
      <div className="public-dashboard-page">
        {loading ? (
          <div className="container loading-message">
            <div className="text-center">
              <img alt="Audimatic Logo" src={logoUrl} width="50" className="m-b-10" />
              <BigMessage className="" icon="fa-spinner fa-2x fa-pulse" message="Loading..." />
            </div>
          </div>
        ) : authRequired ? (
          <div className="public-dashboard-page--gate">
            <div className="public-dashboard-auth-shell">
              <div className="public-dashboard-auth-card">
                <div className="public-dashboard-auth-float">
                  <img alt="" src={logoUrl} width="52" height="52" />
                </div>
                <h1 className="public-dashboard-auth-title">Sign in</h1>
                <p className="public-dashboard-auth-lead">This shared dashboard is protected. Use the credentials you were given.</p>
                <Form layout="vertical" className="public-dashboard-auth-form" onSubmitCapture={this.onAuthSubmit}>
                  {authError && (
                    <Form.Item className="m-b-0">
                      <Alert type="error" message={authError} showIcon />
                    </Form.Item>
                  )}
                  <Form.Item label="Email or username" className="public-dashboard-auth-field">
                    <Input
                      size="large"
                      value={authUsername}
                      onChange={event => this.setState({ authUsername: event.target.value })}
                      autoComplete="username"
                      placeholder="Username"
                    />
                  </Form.Item>
                  <Form.Item label="Password" className="public-dashboard-auth-field">
                    <Input.Password
                      size="large"
                      value={authPassword}
                      onChange={event => this.setState({ authPassword: event.target.value })}
                      autoComplete="current-password"
                      placeholder="Password"
                    />
                  </Form.Item>
                  <Button type="primary" htmlType="submit" size="large" block className="public-dashboard-auth-submit">
                    Sign in
                  </Button>
                </Form>
              </div>
            </div>
          </div>
        ) : dashboard ? (
          <PublicDashboard dashboard={dashboard} />
        ) : (
          <div className="container loading-message">
            <div className="text-center">
              <BigMessage className="" icon="fa-warning" message="Unable to load this dashboard." />
            </div>
          </div>
        )}
        {!authRequired && (
          <div id="footer">
            <div className="text-center">
              <Link href="https://audimatic.net">
                <img alt="Audimatic Logo" src={logoUrl} width="38" />
              </Link>
            </div>
            Powered by <Link href="https://audimatic.net">Audimatic</Link>
          </div>
        )}
      </div>
    );
  }
}

routes.register(
  "Dashboards.ViewShared",
  routeWithApiKeySession({
    path: "/public/dashboards/:token",
    render: pageProps => <PublicDashboardPage {...pageProps} />,
    getApiKey: currentRoute => currentRoute.routeParams.token,
  })
);
